import {BehaviorSubject, Observable} from 'rxjs';
import {Call, StreamVideoClient} from '@stream-io/video-client';
import {StreamSessionMedia} from '../stream/StreamSessionMedia';
import {logUtishaWarn} from '../../../util/log';

export type SessionStatus =
  | 'idle'
  | 'initializing'
  | 'initialized'
  | 'joining'
  | 'joined'
  | 'leaving'
  | 'left'
  | 'error';

export interface IdleState {
  status: 'idle';
}

export interface InitializingState {
  status: 'initializing';
  abortController: AbortController;
  eventId: number;
}

export interface InitializedState {
  status: 'initialized';
  client: StreamVideoClient;
  eventId: number;
}

export interface JoiningState {
  status: 'joining';
  client: StreamVideoClient;
  call: Call;
  eventId: number;
}

export interface JoinedState {
  status: 'joined';
  client: StreamVideoClient;
  call: Call;
  media: StreamSessionMedia;
  eventId: number;
  streamSessionId: string;
}

export interface LeavingState {
  status: 'leaving';
  reason: 'user' | 'error' | 'ended' | 'reconnecting';
  previousState: JoinedState | JoiningState;
}

export interface LeftState {
  status: 'left';
  reason?: 'user' | 'error' | 'ended';
  error?: Error;
}

export interface ErrorState {
  status: 'error';
  error: Error;
  previousStatus: Exclude<SessionStatus, 'error'>;
}

export type SessionState =
  | IdleState
  | InitializingState
  | InitializedState
  | JoiningState
  | JoinedState
  | LeavingState
  | LeftState
  | ErrorState;

/**
 * Valid state transitions for the session lifecycle.
 * This ensures impossible states are unrepresentable.
 */
const VALID_TRANSITIONS: Record<SessionStatus, SessionStatus[]> = {
  'idle': ['initializing'],
  'initializing': ['initialized', 'error', 'idle'],
  'initialized': ['joining', 'leaving', 'error', 'idle'],
  'joining': ['joined', 'error', 'leaving'],
  'joined': ['leaving', 'error'],
  'leaving': ['left', 'error'],
  'left': ['initializing', 'idle'],
  'error': ['initializing', 'idle', 'leaving']
};

/**
 * SessionStateMachine provides explicit, enforceable session lifecycle management.
 *
 * Key guarantees:
 * - Only valid state transitions are allowed
 * - State is immutable - always get a complete snapshot
 * - Cleanup is performed atomically with state transitions
 * - Async operations can be cancelled via AbortController
 */
export class SessionStateMachine {
  private _state = new BehaviorSubject<SessionState>({ status: 'idle' });

  /**
   * Observable of state changes. Use this for reactive updates.
   */
  readonly state$: Observable<SessionState> = this._state.asObservable();

  /**
   * Get current state snapshot (synchronous)
   */
  get currentState(): SessionState {
    return this._state.value;
  }

  /**
   * Get current status (convenience accessor)
   */
  get currentStatus(): SessionStatus {
    return this._state.value.status;
  }

  /**
   * Check if a transition to the target status is valid from current state
   */
  canTransitionTo(targetStatus: SessionStatus): boolean {
    const currentStatus = this.currentStatus;
    return VALID_TRANSITIONS[currentStatus]?.includes(targetStatus) ?? false;
  }

  /**
   * Check if a transition is valid from a specific state
   */
  static canTransition(from: SessionStatus, to: SessionStatus): boolean {
    return VALID_TRANSITIONS[from]?.includes(to) ?? false;
  }

  /**
   * Transition to a new state.
   *
   * @throws Error if transition is invalid
   * @returns The new state
   */
  transition(newState: SessionState): SessionState {
    const currentStatus = this.currentStatus;
    const newStatus = newState.status;

    if (!this.canTransitionTo(newStatus)) {
      throw new SessionStateTransitionError(
        `Invalid state transition: ${currentStatus} -> ${newStatus}`,
        currentStatus,
        newStatus
      );
    }

    this._state.next(newState);
    return newState;
  }

  /**
   * Attempt transition, return success boolean instead of throwing
   */
  tryTransition(newState: SessionState): boolean {
    try {
      this.transition(newState);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Transition to error state from any state.
   * Validates the transition first; logs a warning if the transition is invalid
   * but still applies it because errors should never be silently swallowed.
   */
  transitionToError(error: Error): ErrorState {
    if (!this.canTransitionTo('error')) {
      logUtishaWarn(
        `SessionStateMachine: transitionToError called from '${this.currentStatus}' which is not a valid transition. Applying anyway to avoid swallowing errors.`
      );
    }
    const errorState: ErrorState = {
      status: 'error',
      error,
      previousStatus: this.currentStatus as Exclude<SessionStatus, 'error'>
    };
    this._state.next(errorState);
    return errorState;
  }

  /**
   * Reset to idle state (use with caution - only from terminal states)
   */
  reset(): void {
    if (!this.canTransitionTo('idle')) {
      throw new SessionStateTransitionError(
        `Cannot reset from ${this.currentStatus}`,
        this.currentStatus,
        'idle'
      );
    }
    this._state.next({ status: 'idle' });
  }

  /**
   * Type guards for checking current state type
   */
  isIdle(): this is { currentState: IdleState } {
    return this.currentStatus === 'idle';
  }

  isInitializing(): this is { currentState: InitializingState } {
    return this.currentStatus === 'initializing';
  }

  isInitialized(): this is { currentState: InitializedState } {
    return this.currentStatus === 'initialized';
  }

  isJoining(): this is { currentState: JoiningState } {
    return this.currentStatus === 'joining';
  }

  isJoined(): this is { currentState: JoinedState } {
    return this.currentStatus === 'joined';
  }

  isLeaving(): this is { currentState: LeavingState } {
    return this.currentStatus === 'leaving';
  }

  isLeft(): this is { currentState: LeftState } {
    return this.currentStatus === 'left';
  }

  isError(): this is { currentState: ErrorState } {
    return this.currentStatus === 'error';
  }

  /**
   * Check if session is in an active state (can send/receive media)
   */
  isActive(): boolean {
    return this.currentStatus === 'joined';
  }

  /**
   * Check if session can be joined
   */
  canJoin(): boolean {
    return this.currentStatus === 'initialized';
  }

  /**
   * Check if session can be left
   */
  canLeave(): boolean {
    return ['joined', 'joining', 'error'].includes(this.currentStatus);
  }

  /**
   * Get human-readable state description for debugging
   */
  getStateDescription(): string {
    const state = this.currentState;
    switch (state.status) {
      case 'idle':
        return 'Session idle';
      case 'initializing':
        return `Initializing session for event ${state.eventId}`;
      case 'initialized':
        return `Session initialized for event ${state.eventId}`;
      case 'joining':
        return `Joining call for event ${state.eventId}`;
      case 'joined':
        return `Active session: ${state.streamSessionId} (event ${state.eventId})`;
      case 'leaving':
        return `Leaving session (reason: ${state.reason})`;
      case 'left':
        return state.error
          ? `Session ended with error: ${state.error.message}`
          : `Session ended (reason: ${state.reason || 'unknown'})`;
      case 'error':
        return `Error in ${state.previousStatus}: ${state.error.message}`;
      default:
        return `Unknown state: ${(state as any).status}`;
    }
  }
}

/**
 * Error thrown when an invalid state transition is attempted
 */
export class SessionStateTransitionError extends Error {
  constructor(
    message: string,
    public readonly from: SessionStatus,
    public readonly to: SessionStatus
  ) {
    super(message);
    this.name = 'SessionStateTransitionError';
  }
}

/**
 * Helper to assert state type at runtime
 */
export function assertState<T extends SessionState>(
  state: SessionState,
  expectedStatus: T['status']
): asserts state is T {
  if (state.status !== expectedStatus) {
    throw new Error(`Expected state ${expectedStatus}, got ${state.status}`);
  }
}
